import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useLocale, useSettings } from '../../context';
import { useBottomSheetGestures } from '../../hooks/useBottomSheetGestures';
import { api } from '../../api';
import { pushRoute } from '../../navigation';
import { lockScroll, unlockScroll } from '../../scrollLock';
import BalanceSum from '../BalanceSum';
import WagerStatusBar, { resolveWagerFromSources } from '../WagerStatusBar';
import { formatBalanceParts } from '../../utils/balanceDisplay';
import {
  IconAccountBets,
  IconAccountBonuses,
  IconAccountPromos,
  IconAccountCashier,
  IconAccountCircleFilled,
  IconAccountProfile,
  IconAccountSecurity,
  IconAccountSupport,
  IconBellFilled,
  IconChevronRight,
  IconHide,
  IconLogOut,
  IconPlusCircle,
  IconShow,
} from '../VurgunIcons';
import { IconClose } from './MobileIcons';
import MobileAccountSheetView, {
  formatSheetBalance,
  getSheetViewTitleKey,
  sheetViewShowsBalance,
} from './MobileAccountSheetView';

function IconBonusSaldo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M5.95721 1.21952C7.33046 0.521666 8.54341 0.176842 10.1742 0.0207022C10.525 -0.0128731 11.5216 -0.0041461 11.96 0.0363076C13.3706 0.166525 14.5582 0.502364 15.8328 1.13152C17.0522 1.73347 17.9638 2.39638 18.8931 3.35721C19.7312 4.22359 20.328 5.0677 20.8642 6.14473C21.6807 7.78498 22.0581 9.52995 21.9928 11.3634C21.9321 13.067 21.543 14.5526 20.7655 16.0499C19.9061 17.7048 18.6082 19.1464 17.0376 20.1905C16.4656 20.5708 15.353 21.1279 14.7157 21.3531C12.613 22.0963 10.4545 22.1997 8.30239 21.6604C7.48223 21.4548 6.95988 21.2619 6.15085 20.8654C4.99139 20.2971 4.1695 19.7063 3.22239 18.7601C2.26483 17.8034 1.68482 16.9949 1.09267 15.7914C0.569287 14.7277 0.234292 13.6185 0.0539929 12.3522C-0.0179976 11.8466 -0.0179976 10.1494 0.0539929 9.64382C0.157611 8.91622 0.327152 8.18225 0.540585 7.53731C0.805009 6.7383 1.35572 5.60671 1.80638 4.93641C2.87075 3.3533 4.33048 2.04614 5.95721 1.21952ZM19.7287 6.76125C18.2408 3.70401 15.2508 1.6349 11.8739 1.32567C9.30742 1.09064 6.70604 1.9136 4.70932 3.59224C2.56041 5.39881 1.26694 8.18156 1.26694 10.998C1.26694 13.8133 2.56665 16.6089 4.70932 18.4023C6.11454 19.5784 7.7315 20.3102 9.59332 20.6126C10.254 20.7199 11.7288 20.72 12.3903 20.6128C13.475 20.4369 14.3013 20.1792 15.2303 19.7272C17.2105 18.7637 18.7685 17.2077 19.7287 15.2348C20.1822 14.3028 20.4397 13.4785 20.6157 12.3952C20.7229 11.7349 20.7229 10.2612 20.6157 9.60083C20.4397 8.51757 20.1822 7.69319 19.7287 6.76125ZM11.5643 6.59988C11.3047 6.1809 10.6952 6.1809 10.4356 6.59988L9.05039 8.83569C8.99637 8.92288 8.92288 8.99637 8.83569 9.05039L6.59988 10.4356C6.1809 10.6952 6.1809 11.3047 6.59988 11.5643L8.83569 12.9495C8.92288 13.0035 8.99637 13.077 9.05039 13.1642L10.4356 15.4C10.6952 15.819 11.3047 15.819 11.5643 15.4L12.9495 13.1642C13.0035 13.077 13.077 13.0035 13.1642 12.9495L15.4 11.5643C15.819 11.3047 15.819 10.6952 15.4 10.4356L13.1642 9.05039C13.077 8.99637 13.0035 8.92288 12.9495 8.83569L11.5643 6.59988Z" fill="currentColor" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.25 2.25C7.42157 2.25 6.75 2.92157 6.75 3.75V5.25H5.25C4.42157 5.25 3.75 5.92157 3.75 6.75V18.75C3.75 19.5784 4.42157 20.25 5.25 20.25H15.75C16.5784 20.25 17.25 19.5784 17.25 18.75V17.25H18.75C19.5784 17.25 20.25 16.5784 20.25 15.75V3.75C20.25 2.92157 19.5784 2.25 18.75 2.25H8.25ZM17.25 15.75H18.75V3.75H8.25V15.75H17.25ZM5.25 6.75H6.75V18.75H15.75V20.25H5.25V6.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconBack() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.089 4.411a.833.833 0 0 1 0 1.179L8.679 10l4.41 4.411a.833.833 0 1 1-1.179 1.179l-5-5a.833.833 0 0 1 0-1.179l5-5a.833.833 0 0 1 1.179 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function LinkRow({ icon: Icon, label, badge, onClick }) {
  return (
    <button type="button" className="mobile-account-sheet__link" onClick={onClick}>
      <span className="mobile-account-sheet__link-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="mobile-account-sheet__link-text">{label}</span>
      {badge > 0 && (
        <span className="mobile-account-sheet__link-counter" aria-label={String(badge)}>{badge}</span>
      )}
      <span className="mobile-account-sheet__link-chevron" aria-hidden="true">
        <IconChevronRight />
      </span>
    </button>
  );
}

const MAIN_VIEW = { id: 'main' };

function sectionToInitialView(section) {
  switch (section) {
    case 'cashier':
      return { id: 'cashier', tab: 'deposit' };
    case 'bets':
      return { id: 'bets' };
    case 'profile':
    case 'verification':
      return { id: 'profile' };
    case 'notifications':
      return { id: 'notifications' };
    case 'security':
      return { id: 'security' };
    case 'support':
      return { id: 'support' };
    default:
      return MAIN_VIEW;
  }
}

function stackForSection(section) {
  const view = sectionToInitialView(section);
  if (view.id === 'main') return [MAIN_VIEW];
  return [MAIN_VIEW, view];
}

export default function MobileAccountSheet({
  open,
  onClose,
  balance,
  onNavigate,
  onLogout,
  unreadNotifications = 0,
  onUnreadChange,
  fullPage = false,
  initialSection,
}) {
  const { user, token, setSession } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const { settings, updateSettings } = useSettings();
  const [accountNumber, setAccountNumber] = useState(null);
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState(null);
  const [stack, setStack] = useState(() => stackForSection(initialSection));
  const stackRef = useRef(stack);
  const [mounted, setMounted] = useState(fullPage);
  const [visible, setVisible] = useState(fullPage);
  const [enterAnim, setEnterAnim] = useState(false);
  const prevDepthRef = useRef(stack.length);

  useEffect(() => {
    if (!fullPage) return;
    setStack(stackForSection(initialSection));
    prevDepthRef.current = stackForSection(initialSection).length;
    setEnterAnim(false);
  }, [fullPage, initialSection]);

  useEffect(() => {
    stackRef.current = stack;
    const depth = stack.length;
    if (depth > prevDepthRef.current) {
      setEnterAnim(true);
    } else {
      setEnterAnim(false);
    }
    prevDepthRef.current = depth;
  }, [stack]);

  useEffect(() => {
    if (fullPage) return undefined;
    if (open) {
      const nextStack = stackForSection(initialSection);
      setStack(nextStack);
      prevDepthRef.current = nextStack.length;
      setEnterAnim(false);
      setMounted(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    return undefined;
  }, [fullPage, open, initialSection]);

  useEffect(() => {
    if (fullPage) return undefined;
    if (!visible && mounted) {
      const timer = window.setTimeout(() => setMounted(false), 360);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [visible, mounted]);

  const currentView = stack[stack.length - 1];
  const isMain = currentView.id === 'main';

  const handleClose = useCallback(() => {
    if (fullPage) {
      setStack([MAIN_VIEW]);
      onClose();
      return;
    }
    setStack([MAIN_VIEW]);
    onClose();
  }, [fullPage, onClose]);

  const gesturesOpen = !fullPage && open && (visible || mounted);

  const {
    sheetRef,
    scrollRef,
    sheetStyle,
    overlayStyle,
    dragging,
    dragHandlers,
  } = useBottomSheetGestures({
    open: gesturesOpen,
    onClose: handleClose,
    dismissEnabled: !fullPage,
  });

  const loadSummary = useCallback(async () => {
    if (!token) return null;
    try {
      const data = await api.accountSummary(token);
      setAccountNumber(data.accountNumber || user?.id || null);
      setSummary(data);
      onUnreadChange?.(data.unread_notifications ?? 0);
      return data;
    } catch {
      setAccountNumber(user?.id || null);
      return null;
    }
  }, [token, user?.id, onUnreadChange]);

  const sheetWalletSig = [
    Number(user?.bonus_balance || 0),
    String(user?.wallet_mode || 'cash'),
    Number(user?.deposit_wager_required || 0),
    Number(user?.deposit_wager_progress || 0),
    Number(user?.bonus_wager_required || 0),
    Number(user?.bonus_wager_progress || 0),
    user?.wager ? `${user.wager.kind}:${user.wager.required}:${user.wager.progress}` : '',
  ].join(':');

  useEffect(() => {
    if (!mounted) return undefined;
    if (!fullPage) lockScroll();
    if (open || fullPage) loadSummary();
    return () => {
      if (!fullPage) unlockScroll();
    };
  }, [mounted, open, fullPage, loadSummary, sheetWalletSig]);

  useEffect(() => {
    if (!fullPage && !mounted) {
      setCopied(false);
      setStack([MAIN_VIEW]);
      return undefined;
    }
    if (!mounted || (!fullPage && !visible)) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (stackRef.current.length > 1) {
        setStack((items) => (items.length > 1 ? items.slice(0, -1) : items));
        scrollRef.current?.scrollTo(0, 0);
      } else {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullPage, mounted, visible, handleClose, scrollRef]);

  const pushView = (id, options = {}) => {
    setStack((items) => [...items, { id, ...options }]);
    scrollRef.current?.scrollTo(0, 0);
  };

  const popView = () => {
    if (stack.length > 1) {
      setStack((items) => items.slice(0, -1));
      scrollRef.current?.scrollTo(0, 0);
      return;
    }
    if (fullPage && isMain) {
      handleClose();
      return;
    }
    handleClose();
  };

  const onOverlayClick = () => {
    if (stack.length > 1) popView();
    else handleClose();
  };

  const onSummaryUpdated = async () => {
    const data = await loadSummary();
    if (data && user) {
      setSession(token, { ...user, balance: Number(data.balance) });
    }
  };

  const onProfileUpdated = (updated) => {
    if (updated) setSession(token, { ...user, ...updated, balance: Number(updated.balance) });
    loadSummary();
  };

  const copyAccount = async () => {
    const value = String(accountNumber || user?.id || '');
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (!user || !mounted) return null;

  const displayAccount = accountNumber || user.id || '—';
  const displayBalance = summary?.balance ?? balance ?? user.balance;
  const sheetBonusBalance = Number(
    summary?.bonus_balance ?? summary?.bonusBalance ?? user?.bonus_balance ?? user?.bonusBalance ?? 0,
  );
  const showSheetBonus = Number.isFinite(sheetBonusBalance) && sheetBonusBalance > 0.009;
  const sheetBonusParts = formatBalanceParts(sheetBonusBalance, settings.hideBalance);
  const sheetWager = resolveWagerFromSources(summary, user);
  const titleKey = getSheetViewTitleKey(currentView, summary);

  const sheetPanel = (
    <div
      id={fullPage ? undefined : 'bottomSheet'}
      ref={sheetRef}
      className={`mobile-account-sheet${fullPage ? ' mobile-account-sheet--page' : ''}${visible ? ' is-visible' : ''}${dragging ? ' is-dragging' : ''}${isMain ? '' : ' mobile-account-sheet--nested'}`}
      style={fullPage ? undefined : sheetStyle}
      onClick={fullPage ? undefined : (event) => event.stopPropagation()}
      role="dialog"
      aria-modal={fullPage ? undefined : 'true'}
      aria-label={tr(titleKey)}
      {...(fullPage ? {} : dragHandlers)}
    >
      {!fullPage && <div className="mobile-account-sheet__thumb" aria-hidden="true" />}

      <div className="mobile-account-sheet__body">
          {isMain ? (
            <header className="mobile-account-sheet__header">
              <span className="mobile-account-sheet__header-avatar" aria-hidden="true">
                <IconAccountCircleFilled />
              </span>
              <div className="mobile-account-sheet__header-info">
                <div className="mobile-account-sheet__title">{tr('account.menu.profile')}</div>
                <div className="mobile-account-sheet__account-row">
                  <span className="mobile-account-sheet__account-label">
                    {tr('mobile.account.number', { id: displayAccount })}
                  </span>
                  <button
                    type="button"
                    className="mobile-account-sheet__copy"
                    onClick={copyAccount}
                    aria-label={tr('mobile.account.copy')}
                  >
                    <IconCopy />
                  </button>
                  {copied && (
                    <span className="mobile-account-sheet__copied">{tr('mobile.account.copied')}</span>
                  )}
                </div>
              </div>
              <button type="button" className="mobile-account-sheet__close" onClick={handleClose} aria-label="Close">
                <IconClose />
              </button>
            </header>
          ) : (
            <header className="mobile-account-sheet__subheader">
              <button type="button" className="mobile-account-sheet__back" onClick={popView} aria-label={tr('account.payments.back')}>
                <IconBack />
              </button>
              <div className="mobile-account-sheet__subheader-info">
                <div className="mobile-account-sheet__subheader-title">{tr(titleKey)}</div>
                {sheetViewShowsBalance(currentView) && (
                  <div className="mobile-account-sheet__subheader-desc">
                    {formatSheetBalance(displayBalance, tr)}
                  </div>
                )}
              </div>
              <button type="button" className="mobile-account-sheet__close" onClick={handleClose} aria-label="Close">
                <IconClose />
              </button>
            </header>
          )}

          <div
            className={`mobile-account-sheet__main${enterAnim && !isMain ? ' mobile-account-sheet__main--nested-enter' : ''}`}
            ref={scrollRef}
            key={`${currentView.id}:${currentView.tab || ''}:${currentView.methodId || ''}`}
          >
            {isMain ? (
              <>
                <div className={`mobile-account-sheet__balance-block${showSheetBonus ? ' mobile-account-sheet__balance-block--with-bonus' : ''}`}>
                  <div className="mobile-account-sheet__balance-row">
                    <div className="mobile-account-sheet__balance-content">
                      <span className="mobile-account-sheet__balance-caption">{tr('account.overview.balance')}</span>
                      <BalanceSum
                        amount={displayBalance}
                        hidden={settings.hideBalance}
                        faderHeight={30}
                        className={`mobile-account-sheet__balance-value${settings.hideBalance ? ' is-hidden' : ''}`}
                      />
                      <button
                        type="button"
                        className="mobile-account-sheet__balance-hide"
                        aria-label={settings.hideBalance ? tr('header.showBalance') : tr('header.hideBalance')}
                        aria-pressed={settings.hideBalance}
                        onClick={() => updateSettings({ hideBalance: !settings.hideBalance })}
                      >
                        {settings.hideBalance ? <IconShow /> : <IconHide />}
                      </button>
                    </div>

                    <button
                      type="button"
                      className="mobile-account-sheet__deposit"
                      onClick={() => pushView('cashier', { tab: 'deposit' })}
                    >
                      <span className="mobile-account-sheet__deposit-icon" aria-hidden="true">
                        <IconPlusCircle />
                      </span>
                      <span>{tr('account.menu.deposit')}</span>
                    </button>

                    {showSheetBonus ? (
                      <>
                        <span className="mobile-account-sheet__balance-sep" aria-hidden="true" />
                        <div className="mobile-account-sheet__bonus-row">
                          <button
                            type="button"
                            className="mobile-account-sheet__bonus-btn"
                            onClick={() => {
                              handleClose();
                              onNavigate?.('promos');
                              pushRoute('promos', { promosKind: 'bonuses' });
                            }}
                          >
                            <span className="mobile-account-sheet__bonus-icon" aria-hidden="true">
                              <IconBonusSaldo />
                            </span>
                            <span className="mobile-account-sheet__bonus-meta">
                              <span className="mobile-account-sheet__bonus-caption">
                                {tr('account.overview.bonusBalance')}
                              </span>
                              <span className={`mobile-account-sheet__bonus-value${settings.hideBalance ? ' is-hidden' : ''}`}>
                                {sheetBonusParts.full}
                              </span>
                            </span>
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>

                {sheetWager ? (
                  <div className="mobile-account-sheet__wager">
                    <WagerStatusBar
                      wager={sheetWager}
                      onClick={() => {
                        handleClose();
                        onNavigate?.('promos');
                        pushRoute('promos', { promosKind: 'bonuses' });
                      }}
                    />
                  </div>
                ) : null}

                <nav className="mobile-account-sheet__links" aria-label={tr('header.account')}>
                  <LinkRow
                    icon={IconAccountCashier}
                    label={tr('account.cashier.withdraw')}
                    onClick={() => pushView('cashier', { tab: 'withdraw' })}
                  />
                  <LinkRow
                    icon={IconAccountBets}
                    label={tr('account.menu.history')}
                    onClick={() => pushView('bets')}
                  />
                  <LinkRow
                    icon={IconAccountPromos}
                    label={tr('header.promos')}
                    onClick={() => { handleClose(); onNavigate?.('promos'); pushRoute('promos'); }}
                  />
                  <LinkRow
                    icon={IconAccountBonuses}
                    label={tr('account.nav.bonuses')}
                    onClick={() => {
                      handleClose();
                      onNavigate?.('promos');
                      pushRoute('promos', { promosKind: 'bonuses' });
                    }}
                  />
                  <LinkRow
                    icon={IconAccountSupport}
                    label={tr('account.menu.requests')}
                    onClick={() => pushView('support')}
                  />
                  <LinkRow
                    icon={IconBellFilled}
                    label={tr('account.nav.notifications')}
                    badge={unreadNotifications}
                    onClick={() => pushView('notifications')}
                  />
                  <LinkRow
                    icon={IconAccountProfile}
                    label={tr('account.nav.profile')}
                    onClick={() => pushView('profile')}
                  />
                  <LinkRow
                    icon={IconAccountSecurity}
                    label={tr('account.menu.responsible')}
                    onClick={() => pushView('security')}
                  />
                  <LinkRow
                    icon={IconAccountBonuses}
                    label={tr('account.menu.vip')}
                    onClick={() => { handleClose(); onNavigate?.('vip'); pushRoute('vip'); }}
                  />
                  <LinkRow
                    icon={IconAccountSupport}
                    label={tr('support.liveChat')}
                    onClick={() => {
                      handleClose();
                      window.dispatchEvent(new CustomEvent('vb-support-open'));
                    }}
                  />
                  <span className="mobile-account-sheet__separator" aria-hidden="true" />
                  <LinkRow
                    icon={IconLogOut}
                    label={tr('header.logout')}
                    onClick={() => { handleClose(); onLogout?.(); }}
                  />
                </nav>
              </>
            ) : (
              <MobileAccountSheetView
                view={currentView}
                summary={summary}
                onSummaryUpdated={onSummaryUpdated}
                onProfileUpdated={onProfileUpdated}
                onCashierDeposit={() => {
                  setStack((items) => [...items.slice(0, -1), { id: 'cashier', tab: 'deposit' }]);
                  scrollRef.current?.scrollTo(0, 0);
                }}
                onSelectDepositMethod={(methodId) => {
                  pushView('cashier', { tab: 'deposit', methodId });
                }}
                onClearDepositMethod={() => {
                  if (currentView.methodId && stack.length > 1) {
                    popView();
                    return;
                  }
                  setStack((items) => {
                    const next = [...items];
                    const last = next[next.length - 1];
                    if (last?.id === 'cashier') {
                      next[next.length - 1] = { id: 'cashier', tab: 'deposit' };
                    }
                    return next;
                  });
                  scrollRef.current?.scrollTo(0, 0);
                }}
                onOpenSupport={() => pushView('support')}
                onGoHelp={() => {
                  handleClose();
                  onNavigate?.('help');
                  pushRoute('help');
                }}
                onCreateRequest={() => pushView('support-new')}
                onSelectRequestTemplate={(templateId) => pushView('support-form', { templateId })}
                onRequestCancel={popView}
                onRequestDone={() => {
                  setStack((items) => {
                    const idx = items.findIndex((item) => item.id === 'support');
                    return idx >= 0 ? items.slice(0, idx + 1) : [{ id: 'support' }];
                  });
                  scrollRef.current?.scrollTo(0, 0);
                }}
                onNavigateProfilePanel={(panel) => pushView(`profile-${panel}`)}
                onOpenResponsibleDeposit={() => pushView('security-deposit')}
                onOpenResponsibleSession={() => pushView('security-session')}
                onProfilePasswordDone={popView}
                onNavigate={(page, opts) => {
                  handleClose();
                  onNavigate?.(page, opts);
                }}
                onOpenSheetView={(id, options) => pushView(id, options)}
              />
            )}
          </div>
        </div>
      </div>
  );

  const content = fullPage ? (
    <div className="mobile-account-page">{sheetPanel}</div>
  ) : (
    <div
      className={`mobile-account-sheet__overlay${visible ? ' is-open' : ''}`}
      style={overlayStyle}
      onClick={visible ? onOverlayClick : undefined}
      aria-hidden={!visible}
    >
      {sheetPanel}
    </div>
  );

  return fullPage ? content : createPortal(content, document.body);
}
